import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    ShoppingCart,
    Package,
    ClipboardList,
    User,
    Settings,
    LogOut,
    ChevronLeft,
    ChevronRight,
    BarChart3,
    Users,
    HelpCircle,
    Heart,
    Activity,
    Wallet,
    Shield,
    Home
} from 'lucide-react';
import logoNew from '../../assets/logo-new.png';
import { useAuthStore } from '../../store/authStore';
import { useCartStore } from '../../store/cartStore';
import './Sidebar.scss';

interface SidebarProps {
    isCollapsed: boolean;
    onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const cartItemsCount = useCartStore(state => state.getTotalItems());
    const isAdmin = user?.role === 'admin';

    type MenuItem = {
        path: string;
        icon: React.FC<{ size?: number; className?: string }>;
        label: string;
        badge?: number;
    };

    const customerMenuItems: MenuItem[] = [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/products', icon: Package, label: 'Ürünler' },
        { path: '/favorites', icon: Heart, label: 'Favorilerim' },
        { path: '/cart', icon: ShoppingCart, label: 'Sepet', badge: cartItemsCount > 0 ? cartItemsCount : undefined },
        { path: '/orders', icon: ClipboardList, label: 'Siparişlerim' },
    ];

    const adminMenuItems: MenuItem[] = [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/admin/orders', icon: ClipboardList, label: 'Siparişler' },
        { path: '/admin/stock', icon: Package, label: 'Ürünler' },
        { path: '/admin/customers', icon: Users, label: 'Müşteriler' },
        { path: '/admin/reports', icon: BarChart3, label: 'Raporlar & Analytics' },
        { path: '/admin/campaigns', icon: Wallet, label: 'Kampanyalar' },
        { path: '/admin/membership', icon: Shield, label: 'Üyelik Sistemi' },
        { path: '/admin/accounting', icon: Wallet, label: 'Muhasebe' },
    ];

    const menuItems = isAdmin ? adminMenuItems : customerMenuItems;

    return (
        <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            {/* Floating Toggle Button (Collapsed Only) */}
            <button className="toggle-btn-floating" onClick={onToggle}>
                <ChevronRight size={16} />
            </button>

            {/* Logo */}
            <div className="sidebar-header">
                <div className="logo">
                    {!isCollapsed && (
                        <div className="logo-image-container">
                            <img src={logoNew} alt="Cheesecake İstanbul" className="logo-img" />
                        </div>
                    )}
                    {isCollapsed && <div className="logo-icon small">🍰</div>}
                </div>
                {!isCollapsed && (
                    <button className="toggle-btn" onClick={onToggle}>
                        <ChevronLeft size={18} />
                    </button>
                )}
            </div>

            {/* Navigation */}
            <nav className="sidebar-nav">
                <ul className="nav-list">
                    {menuItems.map(item => (
                        <li key={item.path}>
                            <NavLink
                                to={item.path}
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                title={isCollapsed ? item.label : undefined}
                            >
                                <item.icon size={20} className="nav-icon" />
                                {!isCollapsed && <span className="nav-label">{item.label}</span>}
                                {item.badge && (
                                    <span className="nav-badge">{item.badge}</span>
                                )}
                            </NavLink>
                        </li>
                    ))}
                </ul>

                {/* Secondary Nav */}
                <ul className="nav-list secondary">
                    {isAdmin && (
                        <li>
                            <button
                                onClick={() => navigate('/')}
                                className="nav-link"
                                title={isCollapsed ? 'Siteye Dön' : undefined}
                                style={{ cursor: 'pointer', width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                            >
                                <Home size={20} className="nav-icon" />
                                {!isCollapsed && <span className="nav-label">Siteye Dön</span>}
                            </button>
                        </li>
                    )}
                    <li>
                        <button
                            onClick={() => {
                                // Korunacak değerleri sakla
                                const setupComplete = localStorage.getItem('cheesecake-setup-complete');
                                const userPreferences = localStorage.getItem('cheesecake-preferences');
                                const themeSettings = localStorage.getItem('cheesecake-theme');
                                const userSettings = localStorage.getItem('cheesecake-user-settings');

                                // Temizle
                                localStorage.clear();
                                sessionStorage.clear();

                                // Korunan değerleri geri yükle
                                if (setupComplete) localStorage.setItem('cheesecake-setup-complete', setupComplete);
                                if (userPreferences) localStorage.setItem('cheesecake-preferences', userPreferences);
                                if (themeSettings) localStorage.setItem('cheesecake-theme', themeSettings);
                                if (userSettings) localStorage.setItem('cheesecake-user-settings', userSettings);

                                // Sonra login'e git
                                window.location.replace('/login');
                            }}
                            className="nav-link logout-btn"
                            title={isCollapsed ? 'Çıkış Yap' : undefined}
                            style={{ cursor: 'pointer', width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                        >
                            <LogOut size={20} className="nav-icon" />
                            {!isCollapsed && <span className="nav-label">Çıkış Yap</span>}
                        </button>
                    </li>
                </ul>
            </nav>

            {/* Footer */}
            {!isCollapsed && (
                <div className="sidebar-footer">
                    <p>© 2026 Cheesecake İstanbul</p>
                    <p className="version">v1.2.0</p>
                </div>
            )}
        </aside>
    );
};
